import { useCallback, useEffect, useState } from "react";
import { CheckCircle, Warning } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ImageSlot } from "@/components/ImageUpload";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function InspectionPanel() {
  const [vehicles, setVehicles] = useState([]);
  const [vehicleId, setVehicleId] = useState("");
  const [photos, setPhotos] = useState({ front: null, back: null, left: null, right: null, odometer: null });
  const [today, setToday] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(() => {
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
    api.get("/inspections/today").then((r) => setToday(r.data)).catch(() => {});
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const allSet = Object.values(photos).every(Boolean);

  const submit = async (e) => {
    e.preventDefault();
    if (!allSet) {
      toast.error("All 5 photos are required (4 exterior + odometer)");
      return;
    }
    setBusy(true);
    try {
      await api.post("/inspections", { vehicle_id: vehicleId, photos });
      toast.success("Daily inspection submitted");
      setPhotos({ front: null, back: null, left: null, right: null, odometer: null });
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (today?.done) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-sm border border-emerald-500/40 bg-emerald-500/5 p-12 text-center" data-testid="inspection-done">
        <CheckCircle size={48} weight="fill" className="text-emerald-400" />
        <h3 className="font-display text-xl font-bold">Inspection Complete</h3>
        <p className="text-sm text-muted-foreground">Daily vehicle inspection already submitted for {today.date}.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-6 rounded-sm border border-border bg-card p-6 sm:p-8" data-testid="inspection-form">
      <div>
        <h3 className="font-display text-xl font-bold tracking-tight">Daily Vehicle Inspection</h3>
        <p className="mt-1 flex items-center gap-2 text-sm text-amber-400">
          <Warning size={16} /> Required daily — 4 exterior photos + 1 odometer photo.
        </p>
      </div>
      <div className="space-y-2">
        <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Vehicle</Label>
        <Select value={vehicleId} onValueChange={setVehicleId}>
          <SelectTrigger data-testid="inspection-vehicle" className="rounded-sm border-input bg-background focus:ring-primary">
            <SelectValue placeholder="Select vehicle" />
          </SelectTrigger>
          <SelectContent className="bg-popover">
            {vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.plate_number}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
        <ImageSlot label="Front" value={photos.front} onChange={(v) => setPhotos({ ...photos, front: v })} testid="insp-front" />
        <ImageSlot label="Back" value={photos.back} onChange={(v) => setPhotos({ ...photos, back: v })} testid="insp-back" />
        <ImageSlot label="Left" value={photos.left} onChange={(v) => setPhotos({ ...photos, left: v })} testid="insp-left" />
        <ImageSlot label="Right" value={photos.right} onChange={(v) => setPhotos({ ...photos, right: v })} testid="insp-right" />
        <ImageSlot label="Odometer" value={photos.odometer} onChange={(v) => setPhotos({ ...photos, odometer: v })} testid="insp-odometer" />
      </div>
      <Button data-testid="inspection-submit" type="submit" disabled={busy}
        className="w-full rounded-sm bg-primary py-6 font-bold uppercase tracking-wider text-primary-foreground hover:bg-amber-400 hover:text-black">
        {busy ? "Submitting..." : "Submit Daily Inspection"}
      </Button>
    </form>
  );
}
